import { Text } from "@solomon-apps/shared/src/components/ui/text";
import {
    Address,
    InputGroup,
    InputGroupAddon,
    InputGroupInput,
    InputGroupText
} from "@solomon-labs/design/components/ui";
import React, { useRef } from "react";

import { useAddressFit } from "@/pages/shared/addressFit";
import { OUTLINE_GROUP_CLASS } from "@/pages/shared/lightbarField";

import type { SubmitReview } from "../steps";

/** The asset the receipt totals. USDv is the only asset discovery surfaces
 *  today; a caller passes another symbol once more assets land. */
export const REVIEW_ASSET_SYMBOL = "USDv";

/** One label width for the fields, so their values start on one line; fits
 *  the longest, "Discovered Holdings". */
const LABEL_WIDTH_CLASS = "w-32";

/** The read-only value treatment: selectable and copyable, never editable. */
const READ_ONLY_VALUE_CLASS = "text-foreground-neutral cursor-default";

/** A one-rung ladder that never shortens: the measure's full width. */
const FULL_ADDRESS = [{ minWidth: 0 }] as const;

/** An address value keeps the numeric face, as `AddressField` does. */
const ADDRESS_VALUE_CLASS = "font-numeric tabular-nums";

/* ── Step 3 — Submit (review receipt) ───────────────────────────────── */

/**
 * The last step's receipt, flat on the page under the Review heading (no
 * card): read-only label-inside fields on the outline interaction (a full
 * box border, no light-bar rail): Address (the source summary the earlier
 * steps show, which this step takes over), Discovered Holdings, Total
 * [asset], Destination (fitted to the field), and Destination Label when it
 * has one.
 *
 * Never collapses (it's the final step); the submit action bar
 * (`SubmitGroup`) stays anchored to the frame bottom in `StepFlowPanel`.
 */
export function ReviewGroup({
    review,
    sourceAddress,
    sourceLabel = "Address",
    assetSymbol = REVIEW_ASSET_SYMBOL
}: {
    review: SubmitReview | null;
    /** The address being added, as entered. */
    sourceAddress: string;
    /** Its field label (the step 1 field's). */
    sourceLabel?: string;
    /** The totalled asset's symbol, for the "Total [asset]" label. */
    assetSymbol?: string;
}): React.ReactElement | null {
    // Unreachable in practice (the step gates require a found + verified
    // source before the last step); guards a Back-edited mid-animation frame.
    if (review === null) return null;

    return (
        <div className="flex flex-col">
            <Text
                variant="h3"
                render={<h3 />}
                className="text-foreground-base pb-4"
            >
                Review
            </Text>
            <div className="flex flex-col gap-3">
                <AddressReviewField
                    label={sourceLabel}
                    address={sourceAddress}
                />
                <ReviewField
                    label="Discovered Holdings"
                    value={String(review.totalPositions)}
                />
                <ReviewField
                    label={`Total ${assetSymbol}`}
                    value={review.totalBalanceLabel}
                />
                <AddressReviewField
                    label="Destination"
                    address={review.rewardsDestinationAddress}
                />
                {review.rewardsDestinationLabel ? (
                    <ReviewField
                        label="Destination Label"
                        value={review.rewardsDestinationLabel}
                    />
                ) : null}
            </div>
        </div>
    );
}

/* ── The field shell: outline group, label inside ───────────────────── */

function ReviewFieldShell({
    label,
    children
}: {
    label: string;
    children: React.ReactNode;
}): React.ReactElement {
    return (
        <InputGroup aria-label={label} className={OUTLINE_GROUP_CLASS}>
            <InputGroupAddon align="inline-start">
                <InputGroupText className={LABEL_WIDTH_CLASS}>
                    {label}
                </InputGroupText>
            </InputGroupAddon>
            {children}
        </InputGroup>
    );
}

/** A plain read-only value: the DS group input, `readOnly`. */
function ReviewField({
    label,
    value
}: {
    label: string;
    value: string;
}): React.ReactElement {
    return (
        <ReviewFieldShell label={label}>
            <InputGroupInput
                aria-label={label}
                readOnly
                type="text"
                value={value}
                className={READ_ONLY_VALUE_CLASS}
            />
        </ReviewFieldShell>
    );
}

/**
 * An address value (the source, the destination): the DS `Address` fitted to
 * the field (`useAddressFit` on its own box). A native input cannot hold the
 * `Address` element, so the value is a focusable read-only textbox in the
 * group's control slot, with the input's own box and type.
 */
function AddressReviewField({
    label,
    address
}: {
    label: string;
    address: string;
}): React.ReactElement {
    const boxRef = useRef<HTMLSpanElement>(null);
    const measureRef = useRef<HTMLSpanElement>(null);
    const tier = useAddressFit(address, boxRef, measureRef);
    return (
        <ReviewFieldShell label={label}>
            <span
                data-slot="input-group-control"
                role="textbox"
                aria-readonly
                aria-label={label}
                tabIndex={0}
                className={`flex h-10 min-w-0 flex-1 items-center px-3 py-2 focus-visible:outline-none ${READ_ONLY_VALUE_CLASS}`}
            >
                <span ref={boxRef} className="relative min-w-0 flex-1">
                    <span
                        aria-hidden
                        className="pointer-events-none invisible absolute top-0 left-0 size-0 overflow-hidden"
                    >
                        <span ref={measureRef} className="block w-max">
                            <Address
                                value={address}
                                tiers={FULL_ADDRESS}
                                showTitle={false}
                                textVariant="small"
                                mono={false}
                                className={`${ADDRESS_VALUE_CLASS} w-max max-w-none`}
                            />
                        </span>
                    </span>
                    <Address
                        value={address}
                        tiers={[tier]}
                        textVariant="small"
                        mono={false}
                        className={`${ADDRESS_VALUE_CLASS} text-foreground-neutral`}
                    />
                </span>
            </span>
        </ReviewFieldShell>
    );
}
